import { Box, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Theme as theme } from '../../../../../../../shared/styles/theme/theme.ts';
import { ImportColumnMapping } from '../../../../services/configuration.service.ts';

/**
 * States which field each column of the file became.
 *
 * Column headers are matched by an AI pass, and until now its verdict was
 * invisible: a column read into the wrong field looked exactly like a column
 * read correctly, and a column nobody recognised looked exactly like a column
 * that was never in the file. Both reached us as "the value does not appear"
 * (WA-3846). Showing the mapping is what makes the difference visible before
 * anything is written.
 */
export const ColumnMapping: FC<IColumnMapping> = ({ columnMapping }) => {
  const { t } = useTranslation();

  if (!columnMapping.length) return null;

  const sheets = [...new Set(columnMapping.map((column) => column.sheet))];

  return (
    <Box sx={{ pb: '20px' }}>
      <Typography variant='subtitle2' sx={{ pb: '8px' }}>
        {t('settings.import_column_mapping_title')}
      </Typography>
      <Stack sx={{ gap: '10px' }}>
        {sheets.map((sheet) => (
          <Stack key={sheet} sx={{ gap: '6px' }}>
            {sheets.length > 1 && (
              <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
                {sheet}
              </Typography>
            )}
            <Stack sx={{ flexDirection: 'row', flexWrap: 'wrap', gap: '6px' }}>
              {columnMapping
                .filter((column) => column.sheet === sheet)
                .map((column, index) => (
                  <Box
                    key={`${sheet}-${index}-${column.fileColumn}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '6px',
                      px: '8px',
                      py: '3px',
                      borderRadius: '4px',
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor: column.field ? 'transparent' : theme.palette.orange.light,
                    }}
                  >
                    <Typography variant='body2'>{column.fileColumn}</Typography>
                    <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
                      {'→'}
                    </Typography>
                    <Typography
                      variant='caption'
                      sx={{
                        color: column.field
                          ? theme.palette.text.secondary
                          : theme.palette.text.primary,
                      }}
                    >
                      {column.field ?? t('settings.import_column_ignored')}
                    </Typography>
                  </Box>
                ))}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export interface IColumnMapping {
  columnMapping: ImportColumnMapping[];
}
